// Sentry error tracking (browser).
//
// Wired to catch the class of failures that hurt this app the most: silent runtime
// errors the shopper hits but never reports. The DSN is supplied per-tenant at runtime
// via window.SENTRY_DSN (set in public/config.<tenant>.js) — a Sentry DSN is a public,
// write-only ingest key, so it is safe to ship in client config. No DSN => no init.
//
// Version note: this app is Vue 2.7, so @sentry/vue MUST stay on the v7 line
// (v8+ dropped Vue 2 support). See package.json pin.
import * as Sentry from '@sentry/vue'
import logger from './logger'

// Auth material that can ride in a URL query/hash on this app: the single-use magic-link
// token (?magiclink=…), password-reset token, api keys, etc. Sentry auto-records every
// navigation + fetch URL as a breadcrumb, so these MUST be redacted before any event
// leaves the browser — otherwise a captured error would exfiltrate a live login token.
const SENSITIVE_PARAM_RE =
  /([?&#](?:magiclink|token|reset|code|key|apikey|api_key|password|pwd|secret)=)[^&#\s]*/gi

export function scrubUrl(value) {
  if (typeof value !== 'string' || value === '') return value
  return value.replace(SENSITIVE_PARAM_RE, '$1[REDACTED]')
}

// Initialize Sentry from runtime window config. Returns true if it actually started.
// Call BEFORE mounting the app so mount-time errors are captured. Passing the Vue
// constructor lets @sentry/vue hook Vue.config.errorHandler and attach component context.
export function initSentry(Vue) {
  const dsn = window.SENTRY_DSN
  if (!dsn) return false

  try {
    Sentry.init({
      Vue,
      dsn,
      environment: window.SENTRY_ENVIRONMENT || 'production',
      release: `ecommerce-app@${process.env.VUE_APP_VERSION || '0'}+${process.env.VUE_APP_COMMIT_HASH || 'local'}`,
      // Error capture only. BrowserTracing is intentionally omitted: it turns the page URL
      // into a transaction name, which would re-introduce the token-leak we scrub for below.
      integrations: [],
      tracesSampleRate: 0,
      sendDefaultPii: false,
      // Drop known transient CLIENT-side failures (flaky mobile connectivity, aborted requests,
      // iOS-Safari service-worker fetch hiccups). These are not app bugs — the API + SW are served
      // correctly (verified) and the next load succeeds. Filtering them keeps real errors visible
      // instead of drowning in mobile-network noise (SV-42/SV-43, 2026-08-25).
      ignoreErrors: [
        'Network Error',                     // axios status-0: request never completed at network level
        'Load failed',                       // Safari generic fetch abort
        'AbortError',
        'The operation was aborted',
        /service-worker\.js.*load failed/i,  // iOS Safari transient SW-registration fetch failure
        /ServiceWorker script .* (failed|load failed)/i,
        /Failed to (register|update) a ServiceWorker/i,
      ],
      beforeBreadcrumb(breadcrumb) {
        if (breadcrumb && breadcrumb.data) {
          const d = breadcrumb.data
          if (d.url) d.url = scrubUrl(d.url)
          if (d.to) d.to = scrubUrl(d.to)
          if (d.from) d.from = scrubUrl(d.from)
        }
        if (breadcrumb && typeof breadcrumb.message === 'string') {
          breadcrumb.message = scrubUrl(breadcrumb.message)
        }
        return breadcrumb
      },
      beforeSend(event) {
        if (event.request) {
          if (event.request.url) event.request.url = scrubUrl(event.request.url)
          if (event.request.query_string) {
            event.request.query_string = scrubUrl(String(event.request.query_string))
          }
        }
        return event
      },
    })

    // Expose the SDK so the existing manual captures in services/errorHandler.js
    // (guarded by `if (window.Sentry)`) start reporting too.
    window.Sentry = Sentry
    return true
  } catch (e) {
    logger.warn('[sentry] init skipped:', e && e.message)
    return false
  }
}
